import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export default function HowItWorks() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const steps = [
    {
      num: '01',
      title: 'POST /borrow',
      description: 'Agent sends a borrow request with token contract address, collateral amount, and desired loan size.',
      code: 'POST /v1/enaibl/borrow\n{ "token": "0x...", "amount": "500000" }',
    },
    {
      num: '02',
      title: 'Auto-verify',
      description: 'enaibl checks deployer (Clanker, Bankr, Zora, Flaunch), market cap, liquidity depth, sell tax. LTV tier assigned automatically.',
      code: '✓ Deployer verified\n✓ MC: $2.4M\n✓ Liquidity: $48K',
    },
    {
      num: '03',
      title: 'ETH delivered',
      description: 'ETH is sent to the agent\'s wallet in seconds. Interest and platform fee are prepaid from the loan amount.',
      code: '✓ 3.52 ETH sent\nto 0x4f...2a',
    },
    {
      num: '04',
      title: 'Repay or auto-liquidate',
      description: 'Agent monitors health via GET /health. When ready, POST /repay to reclaim collateral. Health below 1.0 triggers auto-liquidation.',
      code: 'GET /v1/enaibl/health\n{ "score": 1.82 }',
    },
  ];

  return (
    <div ref={ref} className="relative z-10">
      {/* Background grid */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-10 sm:mb-12 relative z-10"
      >
        <span 
          className="inline-block text-[11px] uppercase tracking-[0.08em] text-white/50 px-4 py-1.5 rounded-full mb-4"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          How It Works
        </span>
        <h3 className="text-[22px] sm:text-[28px] font-semibold text-white">One POST request. Instant ETH.</h3>
        <p className="text-[13px] sm:text-[14px] text-white/40 mt-2">Built for agents that move programmatically.</p>
      </motion.div>

      {/* 4 Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
        {steps.map((step, i) => (
          <motion.div
            key={step.num}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.1 }}
            className="p-5 sm:p-6 rounded-[16px] relative overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {/* Step number watermark */}
            <span 
              className="absolute top-3 sm:top-4 right-3 sm:right-4 text-[40px] sm:text-[48px] font-bold text-white/[0.04] leading-none"
            >
              {step.num}
            </span>

            {/* Code preview */}
            <div 
              className="mb-4 p-3 rounded-lg text-[9px] sm:text-[10px] font-mono text-white/50 whitespace-pre-line"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              {step.code}
            </div>

            <h4 className="text-[14px] sm:text-[16px] font-semibold text-white mb-2">{step.title}</h4>
            <p className="text-[12px] sm:text-[13px] text-white/45 leading-relaxed">{step.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
